import React, { useContext, useEffect, useRef, useState } from "react";
import Papa from "papaparse";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogTitle,
  keyframes,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useNavigate } from "react-router-dom";
import { OrderContext } from "../contexts/OrderContext";
import axios from "axios";

// Filled-in template served from public/templates. It documents every column,
// so keep the column order here and in the file in step with mappedObj below.
const TEMPLATE_FILE = "Bulk_Order_Template.xlsx";

const ExcelOrdersCreation = () => {
  const navigate = useNavigate();

  // Context and Local storage data
  const { storeData, setUpdateOrderStatusToggle } = useContext(OrderContext);
  const {
    mfrstore = [],
    allstatus = [],
    storeInfo = [],
    purities = [],
    finishtypes = [],
  } = storeData;
  const url_prefix = storeInfo?.[0]?.url_prefix || "";
  const userRole = localStorage.getItem("userRole");
  const token = localStorage.getItem("authToken");

  const [existingDesignsArr, setExistingDesignsArr] = useState([]);
  const [excelFile, setExcelFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [ordersDetailsArr, setOrdersDetailsArr] = useState([]);
  const [ordersCreatedCount, setOrdersCreatedCount] = useState(0);
  const [errorOrdersCount, setErrorOrdersCount] = useState(0);
  const [loadingOrdersCount, setLoadingOrdersCount] = useState(1);
  const [loadingErrorOrdersCount, setLoadingErrorOrdersCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [isCreatingBulkOrder, setIsCreatingBulkOrder] = useState(false);
  const [isLoadingOverlay, setIsLoadingOverlay] = useState(false);
  const [errorOrdersArr, setErrorOrdersArr] = useState([]);
  const [showSuccessBackdrop, setShowSuccessBackdrop] = useState(false);
  const [showErrorBackdrop, setShowErrorBackdrop] = useState(false);

  // Snackbar
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Observe excel file input
  const fileInputRef = useRef();

  // For store name dropdown
  const [formData, setFormData] = useState({
    storeName: null,
    files: [],
    folderFiles: [],
  });

  // For store name dropdown options
  const mapStoreOptions = (data = []) =>
    data.map((item) => ({ id: item.id, name: item.name }));

  // Store name dropdown change handler
  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Manufacturer ID
  let mfrId = "";
  if (
    userRole === "Customer" ||
    userRole === "SEMIMFR" ||
    userRole === "SUPERADMIN" ||
    userRole === "COMFR"
  ) {
    mfrId = JSON.parse(localStorage.getItem("selectedMfr"))?.id || "";
  } else if (userRole === "MFR") {
    mfrId = localStorage.getItem("id") || "";
  }

  // Check if both store name and files are updated
  useEffect(() => {
    setIsCreatingBulkOrder(!!(formData.storeName && excelFile));
  }, [formData.storeName, excelFile]);

  // Fetching designs data from design catalogue API
  const existingDesignsFetcher = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/fetchorder/getStockByStoreG/${mfrId}`
      );
      setExistingDesignsArr(response?.data);
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    existingDesignsFetcher();
  }, []);

  // Handle excel/csv file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setExcelFile(file);
    setFileName(file.name);
    setErrorMessage("");
  };

  // Create bulk orders by hitting the api
  const submitOrders = async (orders) => {
    let successCount = 0;
    let errorCount = 0;
    let errorCountArr = [];
    setIsLoadingOverlay(true);

    for (const orderObj of orders) {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/orders/createexcelbulkorder`,
          orderObj,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
      } catch (error) {
        errorCount++;
        setLoadingErrorOrdersCount(errorCount);
        errorCountArr.push(orders.indexOf(orderObj) + 2);
        if (error.status !== 422) {
          setIsLoadingOverlay(false);
          setShowErrorBackdrop(true);
          setSnackbarSeverity("error");
          setSnackbarMessage("Failed to create orders.");
          setOpenSnackbar(true);
        }
      }

      successCount++;
      setLoadingOrdersCount(successCount);
    }

    setTimeout(() => {
      setErrorOrdersCount(errorCount);
      setErrorOrdersArr(errorCountArr);
      setIsLoadingOverlay(false);
      setShowSuccessBackdrop(true);
      setOrdersCreatedCount(successCount);
      setSnackbarSeverity("success");
      setSnackbarMessage("All orders created successfully!");
      setOpenSnackbar(true);
      setLoadingOrdersCount(1);
      setUpdateOrderStatusToggle((prev) => !prev);
    }, 1500);
  };

  // Submit excel and create bulk order handler
  const handleSubmit = async () => {
    if (!formData.storeName || !excelFile) return;

    setErrorMessage("");
    setIsCreatingBulkOrder(false); // Disable the button immediately on click

    const custid = userRole === "Customer" ? localStorage.getItem("id") : "";

    const columnLetters = (index) => {
      let letter = "";
      while (index >= 0) {
        letter = String.fromCharCode((index % 26) + 65) + letter;
        index = Math.floor(index / 26) - 1;
      }
      return letter;
    };

    const mappedObj = {
      A: "storeorderno",
      B: "Order Type",
      C: "orderdate",
      D: "orderDeliveryDate",
      E: "Branch Code",
      F: "Product",
      G: "Design Number",
      H: "VendorDesign",
      I: "DType",
      J: "grossWt",
      K: "selectedItemSize",
      L: "pcs",
      M: "Barcoded Quantity",
      N: "Supplied",
      O: "Balance",
      P: "assignedBy",
      Q: "Work Status",
      R: "Remarks",
      S: "Image",
      T: "SubBrand",
      U: "Make",
      V: "Workstyle",
      W: "Form",
      X: "Finish",
      Y: "Theme",
      Z: "Collection",
      AA: "Description",
      AB: "AssignedRemarks",
      AC: "selectedScrewTypeId",
      AD: "Polish",
      AE: "Metal Color",
      AF: "Purity",
      AG: "HBT Class",
      AH: "Consol Id",
      AI: "selectedItemToleranceFrom",
      AJ: "selectedItemToleranceTo",
      AK: "Matching Order Id",
    };

    const formatDate = (input) => {
      if (!input || typeof input !== "string") return null;
      const [day, month, year] = input.split("/");
      if (!day || !month || !year) return null;
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    };

    const calculateKarigarDeliveryDate = (dateStr, days) => {
      const date = new Date(dateStr);
      // If karigar delivery date is 4 days before order delivery date
      // date.setDate(date.getDate() - days);

      // If karigar delivery date is no of days as per the design catalogue or 10 days after customer order date
      date.setDate(date.getDate() + days);
      return date.toISOString().split("T")[0];
    };

    const processRawData = (rawData) => {
      if (rawData.length <= 1) {
        setErrorMessage(
          "No data available in the uploaded file. Please choose a different file."
        );
        setIsCreatingBulkOrder(true);
        return;
      }

      const formattedDataFromExcel = rawData.slice(1).map((eachRowData) => {
        const obj = {};
        eachRowData.forEach((cell, colIndex) => {
          const colName = columnLetters(colIndex);
          obj[colName] = cell;
        });
        return obj;
      });

      const structuredOrders = formattedDataFromExcel.map((excelDataObj) => {
        let structuredObj = {
          imageBlobURLLink: "",
          selectedDesignNumber: "",
          selectedDesignId: "",
          selectedFinishId: "",
          selectedItemTypeId: "",
          selectedMetalTypeId: "",
          selectedPurityId: "",
          selectedScrewTypeId: "",
          selectedStatusId: "",
          selectedCustmerId: custid || "",
          selectedMFR: mfrId || "",
          selectedStoreId: formData.storeName,
          selectedKarigarId: "",
          selectedItemToleranceFrom: "",
          selectedItemToleranceTo: "",
          selectedItemSize: "",
          orderDeliveryDate: "",
          karigarDeliveryDate: "",
          customername: "",
          pcs: "",
          netWt: "",
          grossWt: "",
          storeorderno: "",
          orderDesc: "",
          weightRange: "",
          sampleWeight: "",
          files: [],
          orderdate: "",
          orderType: "Customer",
        };

        for (const key in mappedObj) {
          if (structuredObj.hasOwnProperty(mappedObj[key])) {
            structuredObj[mappedObj[key]] = excelDataObj[key];
          }
        }

        const polishFromExcel = excelDataObj.AD || "";
        const finishID = finishtypes.find(
          (finish) =>
            finish.name.trim().toLowerCase() ===
            polishFromExcel.trim().toLowerCase()
        )?.id;
        structuredObj.selectedFinishId = finishID || "";

        const purityFromExcel = excelDataObj.AF || "";
        structuredObj.selectedPurityId = purities.find(
          (purity) => purity.name === purityFromExcel.slice(0, 2)
        )?.id;

        const workStatusFromExcel = excelDataObj.Q || "";
        const requiredWorkStatusObj = allstatus.find(
          (status) => status.name === workStatusFromExcel
        );
        structuredObj.selectedStatusId = requiredWorkStatusObj?.id || "";

        const designNumberFromExcel = excelDataObj.G || "";
        const requiredDesignCatalogueObj = existingDesignsArr.find(
          (design) =>
            designNumberFromExcel === design.tagno_alpha ||
            designNumberFromExcel === design.certificateno
        );
        structuredObj.selectedDesignNumber =
          requiredDesignCatalogueObj?.tagno_alpha || "";
        structuredObj.selectedDesignId =
          requiredDesignCatalogueObj?.design || "";
        // structuredObj.selectedItemSize =
        //   requiredDesignCatalogueObj?.s_size_len || "";
        structuredObj.selectedItemTypeId =
          requiredDesignCatalogueObj?.itemtype || "";
        structuredObj.selectedKarigarId =
          requiredDesignCatalogueObj?.supplier || "";
        structuredObj.netWt = requiredDesignCatalogueObj?.netwt || "";
        structuredObj.orderDesc = designNumberFromExcel || "";
        structuredObj.imageBlobURLLink = `https://kumuduorderapp.blob.core.windows.net/${url_prefix}`;

        // Dates
        if (structuredObj.orderdate) {
          structuredObj.orderdate = formatDate(structuredObj.orderdate);
        }
        if (structuredObj.orderDeliveryDate) {
          structuredObj.orderDeliveryDate = formatDate(
            structuredObj.orderDeliveryDate
          );
          structuredObj.karigarDeliveryDate = calculateKarigarDeliveryDate(
            // If karigar delivery date is 4 days before order delivery date
            // structuredObj.orderDeliveryDate,
            // 4,

            // If karigar delivery date is no of days as per the design catalogue or 10 days after customer order date
            structuredObj.orderdate,
            requiredDesignCatalogueObj?.pcs || 10
          );
        }
        // console.log(structuredObj);
        return requiredDesignCatalogueObj === undefined ? [] : structuredObj;
      });

      setOrdersDetailsArr(formattedDataFromExcel);
      return structuredOrders;
    };

    // Read and parse csv file
    const reader = new FileReader();
    reader.onload = async (event) => {
      Papa.parse(event.target.result, {
        header: false,
        skipEmptyLines: true,
        complete: async (result) => {
          let rawData = result.data;
          rawData = rawData.filter(
            (row) =>
              Array.isArray(row) &&
              row.some(
                (cell) =>
                  cell !== null &&
                  cell !== undefined &&
                  String(cell).trim() !== ""
              )
          );

          const structuredOrders = processRawData(rawData);
          // console.log(rawData);
          // console.log(structuredOrders);
          setLoadingErrorOrdersCount(0);
          if (structuredOrders?.length) await submitOrders(structuredOrders);
        },
      });
    };

    reader.readAsText(excelFile);
  };

  // Loading animation
  const dotsAnimation = keyframes`
  0%   { content: "."; }
  16%  { content: ".."; }
  32%  { content: "..."; }
  48%  { content: "...."; }
  64%  { content: "....."; }
  80%  { content: "....."; }
  100% { content: "."; }
`;

  // Reload the page if create more button is clicked
  const resetForm = () => {
    setFormData({ storeName: null, files: [], folderFiles: [] });
    setExcelFile(null);
    setFileName("");
    setOrdersDetailsArr([]);
    setOrdersCreatedCount(0);
    setErrorOrdersCount(0);
    setLoadingOrdersCount(1);
    setLoadingErrorOrdersCount(0);
    setErrorMessage("");
    setErrorOrdersArr([]);
  };

  return (
    <Container maxWidth="lg" className="container" sx={{ py: 4 }}>
      {/* Heading and create one order button */}
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box>
          <h2 className="new-order-heading">Create Multiple New Orders</h2>
          <p className="new-order-note">
            Please fill out the form below to create bulk order.
            <br />
            Fields marked with <span style={{ color: "red" }}>*</span> are
            mandatory.
          </p>
        </Box>
        <Box
          onClick={() => navigate("/order")}
          style={{
            backgroundColor: "#0D7377",
            height: "40px",
            width: "150px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 8,
            color: "#fff",
            cursor: "pointer", // 👈 Add this line
          }}
        >
          Create one order
        </Box>
      </Box>

      {/* Select store dropdown */}
      <Box sx={{ mb: 3 }}>
        <Autocomplete
          options={mapStoreOptions(mfrstore)}
          getOptionLabel={(option) => option.name}
          value={
            formData.storeName
              ? {
                  id: formData.storeName,
                  name:
                    mfrstore.find((mfr) => mfr.id === formData.storeName)
                      ?.name || "",
                }
              : null
          }
          onChange={(e, newValue) =>
            handleFieldChange("storeName", newValue?.id || null)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                <span>
                  Store Name <span style={{ color: "red" }}>*</span>{" "}
                  <span style={{ opacity: "0.5" }}>(Step - 1)</span>
                </span>
              }
              fullWidth
            />
          )}
        />
      </Box>

      {/* Download the template that explains every column */}
      <Box
        sx={{
          mb: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          Not sure what goes in which column? Download the template — it lists
          every column, what to fill in it and where it shows up in the app.
        </Typography>
        <Button
          component="a"
          href={`${process.env.PUBLIC_URL}/templates/${TEMPLATE_FILE}`}
          download={TEMPLATE_FILE}
          variant="outlined"
          startIcon={<DownloadIcon />}
          sx={{ whiteSpace: "nowrap" }}
        >
          Download template
        </Button>
      </Box>

      {/* Excel file submit as csv only */}
      <Box sx={{ mb: 3 }}>
        <label
          htmlFor="file-upload"
          style={{
            border: "2px dashed #0A5C60",
            borderRadius: "12px",
            padding: "30px",
            textAlign: "center",
            backgroundColor: excelFile ? "#e0e0e0" : "#f4f3f8",
            color: "#3e3e3e",
            cursor: excelFile ? "not-allowed" : "pointer",
            display: "block",
            pointerEvents: excelFile ? "none" : "auto",
            opacity: excelFile ? 0.6 : 1,
          }}
        >
          <input
            type="file"
            id="file-upload"
            ref={fileInputRef}
            multiple
            accept=".csv"
            hidden
            onChange={handleFileUpload}
          />
          <Typography component="span" fontWeight={600} color="primary">
            {!excelFile && <span style={{ color: "red" }}>*</span>} Click here
          </Typography>{" "}
          to upload csv file <span style={{ opacity: "0.5" }}>(Step - 2)</span>
        </label>
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 1, opacity: 0.7 }}
        >
          Fill your data into the "Ready To Fill" sheet of the template, then
          save it as CSV (File &gt; Save As &gt; CSV UTF-8) and upload it here.
          Keep the columns in the same order as the template.
        </Typography>
        {excelFile && (
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ color: "green" }}>
              ✓ Uploaded <span style={{ fontStyle: "italic" }}>{fileName}</span>
            </Typography>
            <Button
              variant="text"
              size="small"
              color="error"
              onClick={() => {
                setExcelFile(null);
                setFileName("");
                if (fileInputRef.current) fileInputRef.current.value = null;
              }}
              sx={{ mt: 1, bgcolor: "rgba(224, 206, 206, 1)" }}
            >
              Remove file
            </Button>
          </Box>
        )}
      </Box>

      {/* Submit button */}
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!isCreatingBulkOrder}
        >
          Create bulk order
        </Button>
      </Box>

      {/* Error message */}
      {errorMessage && (
        <Typography
          variant="caption"
          sx={{ color: "red", mt: 2, fontStyle: "italic" }}
          dangerouslySetInnerHTML={{ __html: errorMessage }}
        />
      )}

      {/* Circular progress overlay while creating bulk orders */}
      {isLoadingOverlay && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: 2000,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            gap={4}
            width="100%"
            height="100%"
          >
            <CircularProgress size={60} sx={{ color: "#fff" }} />
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
            >
              <Box
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
              >
                <Typography
                  sx={{ fontSize: 16, fontWeight: 600, color: "white" }}
                >
                  Creating bulk orders
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      width: "1.5em",
                      textAlign: "left",
                      "::after": {
                        content: '""',
                        display: "inline-block",
                        whiteSpace: "pre",
                        animation: `${dotsAnimation} 5s steps(5, end) infinite`,
                      },
                    }}
                  />
                </Typography>
              </Box>
              <Typography
                sx={{ fontSize: 16, fontWeight: 600, color: "white" }}
              >
                Current progress:{" "}
                {`${loadingOrdersCount} / ${ordersDetailsArr.length}`}
              </Typography>
              {loadingErrorOrdersCount > 0 && (
                <Typography
                  sx={{ fontSize: 16, fontWeight: 600, color: "white" }}
                >
                  Order{errorOrdersCount > 1 ? "s" : ""} failed to process:{" "}
                  {loadingErrorOrdersCount}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Show success message or error in excel rows after bulk order creation */}
      <Dialog
        open={showSuccessBackdrop}
        onClose={() => setShowSuccessBackdrop(false)}
      >
        <DialogTitle>
          {ordersCreatedCount} Orders were created successfully
        </DialogTitle>

        {/* Error in excel rows */}
        {errorOrdersCount > 0 && (
          <>
            <DialogTitle>
              <span style={{ color: "#ff6961" }}>{errorOrdersCount} </span>{" "}
              order{errorOrdersCount > 1 ? "s" : ""}{" "}
              {errorOrdersCount > 1 ? "were" : "was"} not created (Refer rows in
              excel:{" "}
              {errorOrdersArr.map((errOrder, index) => (
                <span>
                  {errOrder} {index !== errorOrdersArr.length - 1 ? "," : ""}{" "}
                </span>
              ))}
              )
            </DialogTitle>
          </>
        )}

        {/* Dialog box */}
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-end",
            flexDirection: "column",
          }}
        >
          <Button
            onClick={() => {
              setShowSuccessBackdrop(false);
              resetForm();
            }}
            sx={{ color: "green" }}
          >
            Create More
          </Button>
          <Button onClick={() => navigate("/home")}>Go Home</Button>
        </DialogActions>
      </Dialog>

      {/* Show error message for failed design creation */}
      <Dialog
        open={showErrorBackdrop}
        onClose={() => setShowErrorBackdrop(false)}
      >
        <DialogTitle>
          An unexpected error occured while creating bulk orders. Please try
          again.
        </DialogTitle>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-end",
            flexDirection: "column",
          }}
        >
          <Button
            onClick={() => {
              setShowErrorBackdrop(false);
              setIsCreatingBulkOrder(true);
            }}
            sx={{ color: "blue" }}
          >
            Try Again
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000} // Auto close after 6 seconds
        onClose={() => setOpenSnackbar(false)} // Close the Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{
          "&.MuiSnackbar-root": {
            bottom: "100px !important", // force override snackbar position
          },
        }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ExcelOrdersCreation;
